// tslint:disable: no-eval : JavaScript is returned from the API due to legacy support for Portal V3
import { ApplicationRef, createComponent, EnvironmentInjector, inject, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { ProviderUiActionResponse, ProviderActionKind } from '../../../ecash/models/provider-ui-action-response.model';
import { LoggingService } from '../core/logging.service';
import { PaymentStore, CHECKOUT_SUMMARY_SNAPSHOT_STORAGE_KEY } from './payment.store';
import { CallBackModel } from '../../../ecash/models/callback.model';
import { ApiService } from '../core/api.service';
import { GiftCodeModel } from '../../../purchase/models/giftcode/gift-code.model';
import { BasePayment } from '../../models/base-payment.model';
import { PAYMENT_TEMPLATES, PaymentProviderTemplateOptions } from '../../models/orders/payment-templates.token';
import { OrderItem, OrderSnapshot } from '../../../shared/models/orders/order-item.model';
import { PaymentAdjustment } from '../../models/orders/payment-adjustment.model';
import { AdjustmentType } from '../../models/orders/adjustment-type.enum';
import { OrderRequest } from '../../models/orders/order-request.model';

/* ================================
 * Types
 * ================================ */

/**
 * The totals a server-calculated checkout returns, as much of them as this
 * service needs.
 *
 * Structural on purpose: it matches the marketplace's UnifiedCheckoutSummary
 * without importing it, so this shared service stays independent of the feature
 * modules that call it. All amounts are in minor units.
 */
export interface ServerCalculatedSummary {
  TaxMerchant?: number;
  ShippingMerchant?: number;
  /** Full amount payable, including tax and shipping. */
  BasketTotalMerchant?: number;
}

/**
 * One basket line, as the post-payment summary needs to display it. The price
 * is the line total, not the unit price.
 */
export interface ServerCalculatedBasketLine {
  id: string | number;
  quantity: number;
  /** Line total in minor units. */
  totalPrice: number;
  displayName?: string;
  imageUrl?: string;
}

/* ================================
 * Injection Token
 * ================================ */

/**
 * Used to retrieve the persisted payment request snapshot from session storage for displaying checkout summary after payment esp. retrieval from external sources.
 * This is not intended to be used for creating new payments. Use the getPaymentRequest() from the store instead.
 */
export const CHECKOUT_SUMMARY_SNAPSHOT = new InjectionToken<OrderRequest | null>('CHECKOUT_SUMMARY_SNAPSHOT', {
  providedIn: 'root',
  factory: () => {
    const paymentService = inject(PaymentService);
    const raw = sessionStorage.getItem(CHECKOUT_SUMMARY_SNAPSHOT_STORAGE_KEY);
    if (!raw) return null;

    try {
      const snapshot = JSON.parse(raw);

      if (snapshot.expiresAt && Date.now() > snapshot.expiresAt) {
        paymentService.clearCheckoutSummary();
        return null; // expired
      }

      return snapshot.data;
    } catch {
      paymentService.clearCheckoutSummary();
      return null;
    }
  },
});

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private scripts: any = {};
  protected router = inject(Router);
  private paymentTemplates: Record<string, PaymentProviderTemplateOptions> = inject(PAYMENT_TEMPLATES);
  private envInjector = inject(EnvironmentInjector);
  private appRef = inject(ApplicationRef);
  private paymentStore = inject(PaymentStore);
  protected logService = inject(LoggingService);
  private paymentComponentRef: any;
  private api = inject(ApiService);

  /** Loads an external script with a new <script> tag. */
  loadScript(name: string) {
    this.logService.info('Loading script:', name);
    return new Promise<any>((resolve, reject) => {
      if (this.scripts[name] == null) {
        this.scripts[name] = { loaded: false, src: name };
      }

      if (this.scripts[name].loaded) {
        this.logService.info('    Script has already been previously loaded, skipping...');
        resolve({ script: name, loaded: true, status: 'Already loaded', el: this.scripts[name].el });
      } else {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = name;

        script.onload = () => {
          this.scripts[name].loaded = true;
          this.scripts[name].el = script;
          this.logService.info('    Script loaded successfully');
          resolve({ script: name, loaded: true, status: 'Loaded', el: script });
        };

        script.onerror = (error: any) => {
          this.scripts[name].loaded = false;
          script.remove();
          reject({ script: name, loaded: false, status: JSON.stringify(error) });
        };

        document.getElementsByTagName('head')[0].appendChild(script);
      }
    });
  }

  createOrder(items: OrderItem[], adjustments?: PaymentAdjustment[], giftCodes?: GiftCodeModel[]): OrderSnapshot {
    return this.paymentStore.createOrder(items, adjustments, giftCodes);
  }

  createPayment(
    order: OrderSnapshot,
    paymentResponse: ProviderUiActionResponse,
    divName: string,
    callbackUrl: string,
    successUrl: string,
    failureUrl: string
  ) {
    this.paymentStore.createPayment(order, paymentResponse, callbackUrl, successUrl, failureUrl);
    this.process(paymentResponse, divName, failureUrl);
  }

  /**
   * Registers a payment whose totals came from the server, then actions the
   * provider response - the Checkout V4 counterpart to createPayment.
   *
   * Use this for the `api/checkout` endpoints, which return the tax, shipping
   * and basket total on the order. There is no OrderSnapshot to build and no
   * client-side arithmetic: createOrder's sums could only end up disagreeing
   * with the amount the provider is charging.
   *
   * Pass `summary` and `basket` straight from the checkout response - the
   * mapping to OrderItem and PaymentAdjustment happens here, so callers do not
   * each repeat it. Both are described structurally rather than by importing
   * the marketplace's models, because this service is shared and must not
   * depend on a feature module.
   */
  createServerCalculatedPayment(request: {
    paymentResponse: ProviderUiActionResponse;
    divName: string;
    callbackUrl: string;
    successUrl: string;
    failureUrl: string;
    /** The checkout response's Summary, holding the server's totals. */
    summary?: ServerCalculatedSummary | null;
    /** The basket lines to show on the post-payment summary. */
    basket?: readonly ServerCalculatedBasketLine[];
    /**
     * Overrides the total taken from `summary`. Only needed where the amount
     * charged is not the summary's basket total.
     */
    totalAmount?: number;
  }) {
    this.paymentStore.createServerCalculatedPayment({
      paymentResponse: request.paymentResponse,
      items: this.toOrderItems(request.basket),
      adjustments: this.toAdjustments(request.summary),
      totalAmount: request.totalAmount ?? request.summary?.BasketTotalMerchant ?? 0,
      callbackUrl: request.callbackUrl,
      successUrl: request.successUrl,
      failureUrl: request.failureUrl,
    });

    this.process(request.paymentResponse, request.divName, request.failureUrl);
  }

  /**
   * Maps basket lines to the OrderItem shape the provider components and the
   * post-payment summary pages read.
   *
   * The line total is divided back down, because OrderItem holds a unit price
   * while the basket carries the price for the whole line.
   */
  private toOrderItems(basket?: readonly ServerCalculatedBasketLine[]): OrderItem[] {
    return (basket ?? []).map(
      (line) =>
        new OrderItem(
          line.id,
          line.displayName ?? '',
          line.quantity,
          line.quantity ? line.totalPrice / line.quantity : line.totalPrice,
          line.imageUrl ?? ''
        )
    );
  }

  /**
   * Maps the server's tax and shipping to adjustments.
   *
   * These are display data only: the amount charged comes from the summary's
   * own total, so nothing here is added up to reach it - they exist so the
   * post-payment summary can itemise what the total was made of. Zero values
   * are skipped rather than shown as an empty line.
   */
  private toAdjustments(summary?: ServerCalculatedSummary | null): PaymentAdjustment[] {
    if (!summary) {
      return [];
    }

    const adjustments: PaymentAdjustment[] = [];

    if (summary.TaxMerchant) {
      adjustments.push({ type: AdjustmentType.Tax, amount: summary.TaxMerchant });
    }

    if (summary.ShippingMerchant) {
      adjustments.push({ type: AdjustmentType.Shipping, amount: summary.ShippingMerchant });
    }

    return adjustments;
  }

  clearCheckoutSummary() {
    sessionStorage.removeItem(CHECKOUT_SUMMARY_SNAPSHOT_STORAGE_KEY);
    this.paymentStore.update({ paymentRequest: null });
  }

  /**
   * Process a provider UI action response from the API.
   *
   * `callbackUrl` is for flows that have no order and therefore no payment request in the store
   * (adding a card token, top-up): it is the route the rendered payment component navigates to once
   * the provider returns. Order-based flows go through createPayment() instead and can omit it.
   */
  process(
    paymentResponse: ProviderUiActionResponse,
    divName: string,
    failureUrl: string = '/ecashfailure',
    callbackUrl: string = ''
  ): any {
    this.logService.info(
      'Processing payment response...',
      ProviderActionKind[paymentResponse.ProviderActionInformation.ActionKind]
    );
    this.logService.debug('ProviderUiActionResult:', paymentResponse);
    if (paymentResponse.Success) {
      switch (paymentResponse.ProviderActionInformation.ActionKind) {
        case ProviderActionKind.PostForm:
          this._providerActionResponsePostForm(paymentResponse, divName);
          break;

        case ProviderActionKind.RedirectUrl:
          window.location.href = paymentResponse.ProviderActionInformation.Url;
          break;

        case ProviderActionKind.RenderContent:
          this._providerActionResponseRenderContent(paymentResponse, divName);
          break;

        case ProviderActionKind.IframeRedirect:
          this._providerActionResponseIFrameRedirect(paymentResponse, divName);
          break;

        case ProviderActionKind.RenderSpecificIntegration:
          this._providerRenderSpecificIntegration(paymentResponse, divName, callbackUrl, failureUrl);
          break;

        default:
          throw new Error(
            `No matching handler case found for ActionKind: '${
              ProviderActionKind[paymentResponse.ProviderActionInformation.ActionKind]
            }'`
          );
      }
    } else {
      this.logService.info('Provider response was not successful');
      this.router.navigate([failureUrl], {
        queryParams: { reasonId: paymentResponse.ErrorCode },
      });
    }
  }

  // Private Response Actions Helper Methods

  protected _providerRenderSpecificIntegration(
    paymentResponse: ProviderUiActionResponse,
    divName: string,
    callbackUrl: string = '',
    failureUrl: string = ''
  ) {
    let targetDivName = divName;
    if (targetDivName == null || targetDivName === '') {
      targetDivName = paymentResponse.ProviderActionInformation.TargetName;
    }

    const container = document.getElementById(targetDivName);

    if (!container) {
      this.logService.error(`Container with id '${targetDivName}' not found.`);
      return;
    }

    const context = this.buildContext(paymentResponse, callbackUrl, failureUrl);

    if (!context.providerId) {
      this.logService.error('Provider ID is missing in the payment response context.');
      return;
    }

    if (!context.component) {
      this.logService.error(`No component found for provider ID '${context.providerId}'.`);
      return;
    }

    const componentRef = this.buildComponent(context.component);
    componentRef.instance.orderId = context.orderId;
    componentRef.instance.amount = context.amount; // Should be used for submission to the provider, as it is the real amount coming from the api response. The calculation in the store is for display and consistency purposes across the app.
    componentRef.instance.resources = context.resources;
    componentRef.instance.paymentRequest = context.paymentRequest;

    this.appRef.attachView(componentRef.hostView);
    container.appendChild((componentRef.hostView as any).rootNodes[0]);
    this.paymentComponentRef = componentRef;
  }

  private buildContext(response: ProviderUiActionResponse, callbackUrl: string = '', failureUrl: string = '') {
    const providerId = response.ProviderActionInformation.ProviderId;
    const stored = this.paymentStore.getPaymentRequest();

    // Order-based flows (basket checkout, registration) put a request in the store via
    // createPayment(). Flows with no order (card token, top-up) call process() directly and leave
    // the store empty — or worse, holding a previous checkout's request, since it is persisted to
    // session storage. So when the caller supplies its own callbackUrl, that wins over the store.
    const paymentRequest = callbackUrl
      ? new OrderRequest({
          ...stored,
          providerId,
          orderId: response.OrderId,
          callbackUrl: this.toRouterPath(callbackUrl),
          failureUrl: this.toRouterPath(failureUrl),
        })
      : stored;

    return {
      orderId: response.OrderId,
      amount: response.Amount, // The real amount coming from the api response
      resources: response.ProviderActionInformation.Resources,
      providerId,
      component: this.paymentTemplates[providerId]?.component,
      paymentRequest,
    };
  }

  private buildComponent(component: any) {
    return createComponent<BasePayment>(component, { environmentInjector: this.envInjector });
  }

  /**
   * Reduce an absolute portal url to a Router path.
   *
   * Callers build these as `config.portalUrl + route` because redirect-based providers hand the url
   * to their own server to send the browser back. A client-rendered integration (Braintree Hosted
   * Fields) never leaves the page, so it navigates with Router instead — and Router treats
   * 'http://host/path' as one literal segment, matches nothing, and lands on the 404 page. Strip the
   * origin so it resolves as a route. Values that are already relative pass through untouched.
   */
  private toRouterPath(url: string): string {
    if (!url || !/^https?:\/\//i.test(url)) {
      return url;
    }

    try {
      const parsed = new URL(url);
      return `${parsed.pathname}${parsed.search}` || '/';
    } catch {
      this.logService.error('Could not reduce callback url to a router path:', url);
      return url;
    }
  }

  protected _providerActionResponsePostForm(paymentResponse: ProviderUiActionResponse, divName: string) {
    let pageForm = document.getElementById(divName);
    if (pageForm) {
      pageForm.innerHTML = paymentResponse.ProviderActionInformation.Form;
    }
    let renderForm = document.getElementById(paymentResponse.ProviderActionInformation.FormId) as HTMLFormElement;
    renderForm.submit();
  }

  protected _providerActionResponseIFrameRedirect(paymentResponse: ProviderUiActionResponse, divName: string) {
    let targetDivName = divName;
    if (targetDivName == null || targetDivName === '') {
      targetDivName = paymentResponse.ProviderActionInformation.TargetName;
    }
    let pageForm = document.getElementById(targetDivName);

    if (pageForm == null) {
      pageForm = document.getElementById(paymentResponse.ProviderActionInformation.TargetName);
    }

    if (pageForm) {
      pageForm.innerHTML =
        "<iframe id='" +
        paymentResponse.ProviderActionInformation.FormId +
        "' frameborder=0 src='" +
        paymentResponse.ProviderActionInformation.Url +
        "'></iframe>";
    }

    if (paymentResponse.ProviderActionInformation.Scripts && paymentResponse.ProviderActionInformation.Scripts.length > 0) {
      let promises: Promise<any>[] = paymentResponse.ProviderActionInformation.Scripts.map((x) => this.loadScript(x));
      Promise.all(promises)
        .then(() => {
          eval(paymentResponse.ProviderActionInformation.Javascript);
        })
        .catch((reason) => {
          this.logService.error('Error loading external script(s):', reason);
        });
    } else {
      eval(paymentResponse.ProviderActionInformation.Javascript);
    }
  }

  protected _providerActionResponseRenderContent(paymentResponse: ProviderUiActionResponse, divName: string) {
    let lastItem = null;
    let targetDivName = divName;
    if (targetDivName == null || targetDivName === '') {
      targetDivName = paymentResponse.ProviderActionInformation.TargetName;
    }

    if (targetDivName != null && targetDivName !== '') {
      lastItem = document.getElementById(targetDivName);
      if (lastItem) {
        lastItem.innerHTML = paymentResponse.ProviderActionInformation.Form;
      }
    }

    if (paymentResponse.ProviderActionInformation.Scripts && paymentResponse.ProviderActionInformation.Scripts.length > 0) {
      let promises: Promise<any>[] = paymentResponse.ProviderActionInformation.Scripts.map((x) => this.loadScript(x));
      Promise.all(promises)
        .then(() => {
          eval(paymentResponse.ProviderActionInformation.Javascript);
        })
        .catch((reason) => {
          this.logService.error('Error loading external script(s):', reason);
        });
    } else {
      eval(paymentResponse.ProviderActionInformation.Javascript);
    }
  }

  /**
   * Completes the payment for a given order by calling the ECash callback.
   * Navigates to the success or failure page based on the response.
   */
  completePayment(params: Record<string, any>, callbackUrl: string, successUrl: string, failureUrl: string): Promise<void> {
    const callback = new CallBackModel('', callbackUrl);
    const queryParams = this.cleanQueryParams(params);

    return new Promise<void>((resolve, reject) => {
      this.api.post<CallBackModel>('topup/result', callback).subscribe({
        next: async (response) => {
          try {
            if (response.Success) {
              await this.router.navigate([successUrl], { queryParams });
            } else {
              await this.router.navigate([failureUrl]);
            }
            resolve(response);
          } catch (navError) {
            this.logService.error('Navigation failed:', navError);

            await this.router.navigate([failureUrl]);
            reject(navError);
          }
        },
        error: async (err) => {
          this.logService.error('Payment callback failed:', err);
          await this.router.navigate([failureUrl]);
          reject(err);
        },
      });
    });
  }

  private cleanQueryParams(params: Record<string, any>) {
    return Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null && value !== undefined && value !== '')
    );
  }

  /* Manually destroys the component to trigger ngOnDestroy */
  destroyPaymentComponent() {
    if (this.paymentComponentRef) {
      this.appRef.detachView(this.paymentComponentRef.hostView);
      this.paymentComponentRef.destroy();
      this.paymentComponentRef = null;
      this.scripts = {};
    }
  }
}
